import {
  IContactRespSchema,
  QuoteShareResponse,
  RecordShareBaseParams,
  StripePaymentMethodData,
} from '@monetize/types/app';
import {
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import {
  StripeError,
  StripePaymentElementChangeEvent,
} from '@stripe/stripe-js';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { handleApiErrorToast } from '../../../api/axios';
import {
  shareQueryKeys,
  useCreatePaymentMethodForQuoteShare,
} from '../../../api/shareService';
import {
  MBox,
  MButton,
  MCenterModal,
  MDivider,
  MFormField,
  MRadio,
  MRadioGroup,
  MStack,
  MText,
} from '../../../components/Monetize';
import { MCustomSelect } from '../../../components/Monetize/MCustomSelect/MCustomSelect';
import { PaymentMethodItemRenderer } from '../../../components/PaymentMethods/PaymentMethodItemRenderer';
import { StripePaymentMethodTerms } from '../../../components/Payments/StripePaymentMethodTerms';
import { ROUTES } from '../../../constants';
import { logger } from '../../../services/logger';
import Sentry from '../../../services/sentry';
import { getGroupedAndSortedPaymentMethodOptions } from '../../../utils/payment.utils';
import {
  getRecordShareCaptureContext,
  getRecordShareErrorLogContext,
} from '../../../utils/recordShareLogging';
import {
  achPaymentMethods,
  electronicPaymentMethods,
} from '../quote-share.utils';
import { QuoteShareUxConfig } from '../useQuoteShareData';

function getPaymentMethodBillingDetails(contact: IContactRespSchema) {
  return {
    fullName: contact.fullName || '',
    email: contact.email || '',
    phone: contact.phone || '',
    title: contact.title || '',
    line1: contact.address?.line1 || '',
    line2: contact.address?.line2 || '',
    city: contact.address?.city || '',
    state: contact.address?.state || '',
    postalCode: contact.address?.postalCode || '',
    country: contact.address?.country || '',
  };
}

interface QuoteSharePaymentMethodEditModalProps {
  companyName: string;
  quoteData: QuoteShareResponse;
  authParams: RecordShareBaseParams;
  stripePublicKey: string;
  quoteShareUxConfig: QuoteShareUxConfig;
  /**
   * ELECTRONIC handles all non-ACH payment methods
   * We don't need to differentiate since BE and Stripe manage what actually shows up
   */
  paymentType: 'ELECTRONIC' | 'ACH';
  setPaymentType: (type: 'ELECTRONIC' | 'ACH') => void;
  onSave: (paymentMethodId: string) => void;
  /**
   * Called after a new payment method is created via Stripe. The confirmed
   * SetupIntent is single-use, so the parent regenerates a fresh one to allow
   * adding another card without a page refresh.
   */
  onPaymentMethodCreated: () => void;
  onClose: () => void;
}

export const QuoteSharePaymentMethodEditModal = ({
  companyName,
  quoteData,
  authParams,
  stripePublicKey,
  quoteShareUxConfig,
  paymentType,
  setPaymentType,
  onSave,
  onPaymentMethodCreated,
  onClose,
}: QuoteSharePaymentMethodEditModalProps) => {
  const elements = useElements();
  const stripe = useStripe();
  const queryClient = useQueryClient();

  const [stripeData, setStripeData] = useState<StripePaymentMethodData>();
  const [stripeState, setStripeState] = useState<'LOADING' | 'ERROR' | 'READY'>(
    'LOADING',
  );

  const existingPaymentMethods = quoteData.paymentMethods;
  const hasPaymentMethods = existingPaymentMethods.length > 0;

  const [paymentMethodType, setPaymentMethodType] = useState<
    'new' | 'existing'
  >(hasPaymentMethods ? 'existing' : 'new');
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    string | undefined
  >(
    existingPaymentMethods.find((pm) => pm.isDefault)?.id ??
      existingPaymentMethods[0]?.id,
  );

  const { mutateAsync: createPaymentMethod, isPending: isSaving } =
    useCreatePaymentMethodForQuoteShare();

  const paymentMethodItems = useMemo(
    () => getGroupedAndSortedPaymentMethodOptions(existingPaymentMethods),
    [existingPaymentMethods],
  );

  const handlePaymentFormChangeEvent = ({
    complete,
    empty,
    value,
  }: StripePaymentElementChangeEvent) => {
    setStripeData({ empty, complete, type: value.type });
  };

  const handlePaymentFormLoadError = ({ error }: { error?: StripeError }) => {
    setStripeState('ERROR');
    logger.error('onLoadError', error);
    const logContext = getRecordShareErrorLogContext(authParams, error);
    // The modal renders null in this state, so without a log the user simply
    // sees a payment form that never appears.
    Sentry.logger.error('QuoteSharePage - payment form failed to load', {
      ...logContext,
      stripeErrorType: error?.type ?? '',
      stripeErrorCode: error?.code ?? '',
    });
    // Stripe can fire onLoadError with no error at all, and a StripeError is a
    // plain object rather than an Error — wrapping keeps every load failure in
    // one Sentry issue and chains whatever Stripe sent as the cause.
    Sentry.captureException(
      new Error('Payment form failed to load', { cause: error }),
      getRecordShareCaptureContext(logContext, {
        type: 'QUOTE_SHARE',
        action: 'LOAD_PAYMENT_FORM',
      }),
    );
  };

  const allowElectronic = electronicPaymentMethods.has(
    quoteShareUxConfig.allowedPaymentMethods,
  );
  const allowAch = achPaymentMethods.has(
    quoteShareUxConfig.allowedPaymentMethods,
  );

  async function handleSave() {
    try {
      // ACH just shows wire instructions — nothing to save
      if (paymentType === 'ACH') {
        onClose();
        return;
      }

      // Existing payment method — nothing to create, already saved on the account
      if (paymentMethodType === 'existing' && selectedPaymentMethodId) {
        onSave(selectedPaymentMethodId);
        onClose();
        return;
      }

      // New electronic payment method — create via Stripe + API
      if (!stripe || !elements || !quoteData.paymentGatewayId) {
        return;
      }

      const newPaymentMethod = await createPaymentMethod({
        stripe,
        elements,
        authParams,
        requestData: {
          paymentGatewayId: quoteData.paymentGatewayId,
          paymentMethod: {
            amount: 0,
            paymentMethodName: '',
            billingDetails: getPaymentMethodBillingDetails(
              quoteData.quote.billingContact,
            ),
          },
        },
        returnUrlPath: ROUTES.getQuoteSharePaymentMethodStripeCallbackRoute(
          authParams.tenantId,
          authParams.recordId,
          authParams.token,
        ),
        stripePublicKey,
      });

      // Invalidate the quote cache so it refetches with the new payment method
      queryClient.invalidateQueries({
        queryKey: [
          ...shareQueryKeys.quote(authParams.recordId),
          authParams.tenantId,
          authParams.token,
        ],
      });

      // The SetupIntent we just confirmed is single-use — regenerate a fresh one
      // so the user can add another card without refreshing the page.
      onPaymentMethodCreated();

      if (newPaymentMethod?.id) {
        onSave(newPaymentMethod.id);
      }
      onClose();
    } catch (ex) {
      const logContext = getRecordShareErrorLogContext(authParams, ex);
      Sentry.logger.error('QuoteSharePage - payment method save failed', {
        ...logContext,
        paymentType,
        paymentMethodType,
      });
      Sentry.captureException(
        ex,
        getRecordShareCaptureContext(logContext, {
          type: 'QUOTE_SHARE',
          action: 'SAVE_PAYMENT_METHOD',
          quoteId: authParams.recordId,
        }),
      );
      handleApiErrorToast(ex);
      logger.error(ex);
    }
  }

  if (stripeState === 'ERROR') {
    return null;
  }

  return (
    <MCenterModal
      size="xl"
      isOpen
      onClose={onClose}
      modalTitle="Payment Method"
      closeOnEsc={!isSaving}
      closeOnOverlayClick={false}
      renderFooter={() => (
        <MStack gap={4} direction="row" align="center" justify="right" flex={1}>
          <MButton
            onClick={() => onClose()}
            variant="cancel"
            minW="auto"
            isDisabled={isSaving}
          >
            Cancel
          </MButton>
          <MButton
            data-testid="payment-form-submit"
            variant="primary"
            isLoading={isSaving}
            onClick={handleSave}
            minW="auto"
          >
            Save
          </MButton>
        </MStack>
      )}
    >
      {/* If both are allowed, show radio toggle */}
      {allowElectronic && allowAch && (
        <>
          <MFormField>
            <MRadioGroup
              value={paymentType}
              onChange={(value) =>
                setPaymentType(value as 'ELECTRONIC' | 'ACH')
              }
            >
              <MRadio value="ELECTRONIC">
                <MText fontSize="sm" ml={2} fontWeight="bold">
                  Electronic Payment Method
                </MText>
              </MRadio>
              <MRadio value="ACH" ml={8}>
                <MText fontSize="sm" ml={2} fontWeight="bold">
                  ACH Wire Transfer
                </MText>
              </MRadio>
            </MRadioGroup>
          </MFormField>
          <MDivider mt={2} mb={4} />
        </>
      )}
      {allowElectronic && paymentType === 'ELECTRONIC' && (
        <>
          {hasPaymentMethods && (
            <MRadioGroup
              value={paymentMethodType}
              onChange={(value) =>
                setPaymentMethodType(value as 'new' | 'existing')
              }
            >
              <MBox>
                <MRadio value="existing">
                  <MText fontSize="sm" ml={2} fontWeight="bold">
                    Existing Payment Method
                  </MText>
                </MRadio>
                {paymentMethodType === 'existing' && (
                  <MBox display={{ md: 'flex' }} gap={{ md: 4 }} mt={4} ml={7}>
                    <MFormField label="Payment Method" isRequired>
                      <MCustomSelect
                        items={paymentMethodItems}
                        itemTitle="name"
                        itemValue="id"
                        renderItemContent={PaymentMethodItemRenderer}
                        value={selectedPaymentMethodId}
                        onChange={(value) =>
                          setSelectedPaymentMethodId(value as string)
                        }
                      />
                    </MFormField>
                  </MBox>
                )}
              </MBox>
              <MRadio value="new" my={4}>
                <MText fontSize="sm" ml={2} fontWeight="bold">
                  New Payment Method
                </MText>
              </MRadio>
            </MRadioGroup>
          )}

          {(!hasPaymentMethods || paymentMethodType === 'new') && (
            <MBox ml={hasPaymentMethods ? 7 : undefined}>
              <PaymentElement
                onChange={handlePaymentFormChangeEvent}
                onLoaderStart={() => setStripeState('LOADING')}
                onLoadError={handlePaymentFormLoadError}
                onReady={() => setStripeState('READY')}
                options={{
                  layout: {
                    type: 'accordion',
                    radios: true,
                    defaultCollapsed: false,
                    spacedAccordionItems: true,
                  },
                  terms: {
                    usBankAccount: 'never',
                    card: 'never',
                  },
                  fields: {
                    billingDetails: {
                      name: 'never',
                      email: 'never',
                      phone: 'auto',
                      address: 'auto',
                    },
                  },
                }}
              />

              {stripeData && (
                <StripePaymentMethodTerms
                  containerProps={{ pt: 3 }}
                  companyName={companyName}
                  type={stripeData.type}
                />
              )}
            </MBox>
          )}
        </>
      )}
      {allowAch && paymentType === 'ACH' && quoteShareUxConfig.achContent && (
        <MBox>
          <MText>
            Bank transfers, also known as ACH payments, can take up to five
            business days. To pay via ACH, transfer funds using the following
            bank information.
          </MText>
          <MBox
            className="rich-text"
            dangerouslySetInnerHTML={{
              __html: quoteShareUxConfig.achContent ?? '',
            }}
          />
        </MBox>
      )}
    </MCenterModal>
  );
};
